import { useState, useCallback } from "react";

export type Theme = "default" | "cocoa" | "paper" | "sage";

const STORAGE_KEY = "alkio_theme";
const VALID_THEMES: ReadonlySet<string> = new Set([
  "default",
  "cocoa",
  "paper",
  "sage",
]);

export const THEMES: readonly { id: Theme; label: string }[] = [
  { id: "default", label: "Slate Night" },
  { id: "cocoa", label: "Cocoa" },
  { id: "paper", label: "Paper" },
  { id: "sage", label: "Sage" },
];

function readStoredTheme(): Theme {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw && VALID_THEMES.has(raw)) return raw as Theme;
  return "default";
}

function applyTheme(theme: Theme) {
  if (theme === "default") {
    delete document.documentElement.dataset.theme;
  } else {
    document.documentElement.dataset.theme = theme;
  }
}

export function useTheme() {
  const [theme, setThemeState] = useState<Theme>(readStoredTheme);

  const setTheme = useCallback((t: Theme) => {
    try {
      localStorage.setItem(STORAGE_KEY, t);
    } catch {
      /* quota exceeded */
    }
    applyTheme(t);
    setThemeState(t);
  }, []);

  const resetTheme = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY);
    applyTheme("default");
    setThemeState("default");
  }, []);

  return { theme, setTheme, resetTheme } as const;
}
